import { useCallback, useSyncExternalStore } from 'react';

import { useQueryClient } from '@tanstack/react-query';

// this hook is for subscribing the component to the notification changes
// this is because `conversations` from useQuery has keys passed based on the
// current view (my inbox, all, agents, departments etc),
// so it will not trigger a component rerender when the cache changes
//
// Pass `select` to narrow it: an unchanged selected value notifies nobody.
export const useQueryObserver = <TData, TSelected = TData>(
  queryKey: readonly string[],
  select?: (data: TData) => TSelected
) => {
  const queryClient = useQueryClient();

  const subscribe = useCallback(
    (onStoreChange: () => void) => queryClient.getQueryCache().subscribe(onStoreChange),
    [queryClient]
  );

  const getSnapshot = useCallback(() => {
    const data = queryClient.getQueryData<TData>(queryKey);
    return select ? select(data) : (data as unknown as TSelected);
  }, [queryClient, queryKey, select]);

  return { data: useSyncExternalStore(subscribe, getSnapshot) };
};
